import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProfileSelector } from "@/components/matchmaker/ProfileSelector";
import { ProfileCard } from "@/components/matchmaker/ProfileCard";
import { ProfileDetailModal } from "@/components/matchmaker/ProfileDetailModal";
import { MatchCreationConfirmDialog } from "@/components/matchmaker/MatchCreationConfirmDialog";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { calculateWeightedCompatibility } from "@/utils/weightedCompatibility";
import { PriorityBadges } from "@/components/matchmaker/PriorityBadges";
import { WeightedCompatibilityScore } from "@/components/matchmaker/WeightedCompatibilityScore";
import { Eye, Loader2, HeartHandshake } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { anonymizeProfile } from "@/utils/anonymization";
import { getScoreColor } from "@/utils/compatibilityColors";
import { cn } from "@/lib/utils";
import { useSignedPhotoUrl } from "@/lib/photoUrl";
import { useUserRole } from "@/hooks/useUserRole";

export default function SmartMatcher() {
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [selectedProfileDetails, setSelectedProfileDetails] = useState<any>(null);
  const [matchingProfiles, setMatchingProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedModalProfile, setSelectedModalProfile] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [creatingMatchId, setCreatingMatchId] = useState<string | null>(null);
  const [existingMatches, setExistingMatches] = useState<Record<string, any>>({});
  const [expandedCompatibility, setExpandedCompatibility] = useState<Record<string, boolean>>({});
  const [dismissedMatches, setDismissedMatches] = useState<Set<string>>(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingMatch, setPendingMatch] = useState<{ profileId: string; profileName: string } | null>(null);

  // Handle pre-selected profile from URL params
  useEffect(() => {
    const profileId = searchParams.get('profileId');
    if (profileId) {
      // Only fetch if different from current selection
      if (!selectedProfile || selectedProfile.id !== profileId) {
        // Fetch and set the profile
        supabase
          .from('profiles')
          .select('*')
          .eq('id', profileId)
          .single()
          .then(({ data, error }) => {
            if (data && !error) {
              setSelectedProfile(data);
            }
          });
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedProfile) {
      fetchSelectedProfileDetails();
    } else {
      setSelectedProfileDetails(null);
      setMatchingProfiles([]);
    }
  }, [selectedProfile]);

  const fetchSelectedProfileDetails = async () => {
    if (!selectedProfile) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", selectedProfile.id)
        .single();

      if (error) throw error;
      setSelectedProfileDetails(data);
      await findMatches(data);
    } catch (error) {
      console.error("smart_matcher.fetch_profile_failed");
    } finally {
      setLoading(false);
    }
  };

  const findMatches = async (profile: any) => {
    if (!profile) return;

    try {
      // Fetch opposite gender profiles
      const oppositeGender = profile.gender === "male" ? "female" : "male";
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("gender", oppositeGender)
        .eq("is_active", true)
        .eq("profile_completed", true)
        .neq("id", profile.id);

      if (error) throw error;

      // Calculate weighted compatibility for each profile
      const profilesWithScores = (data || []).map((otherProfile) => {
        // If admin mode, calculate compatibility with anonymized profiles so names in notes are anonymized
        const profile1ForCalc = isAdmin ? anonymizeProfile(profile) : profile;
        const profile2ForCalc = isAdmin ? anonymizeProfile(otherProfile) : otherProfile;
        const compatibility = calculateWeightedCompatibility(profile1ForCalc, profile2ForCalc);
        return {
          ...otherProfile,
          compatibility,
        };
      });

      // Sort by compatibility score
      profilesWithScores.sort((a, b) => b.compatibility.score - a.compatibility.score);
      setMatchingProfiles(profilesWithScores);

      // Fetch existing matches
      const profileIds = profilesWithScores.map(p => p.id);
      const { data: matchesData } = await supabase
        .from("matches")
        .select("profile_1_id, profile_2_id, status")
        .or(`and(profile_1_id.eq.${profile.id},profile_2_id.in.(${profileIds.join(",")})),and(profile_2_id.eq.${profile.id},profile_1_id.in.(${profileIds.join(",")}))`);

      const matchesMap: Record<string, any> = {};
      matchesData?.forEach((match) => {
        const otherId = match.profile_1_id === profile.id ? match.profile_2_id : match.profile_1_id;
        matchesMap[otherId] = match;
      });
      setExistingMatches(matchesMap);

      // Fetch dismissed matches for this profile
      const { data: user } = await supabase.auth.getUser();
      if (user.user) {
        const { data: dismissedData } = await supabase
          .from("dismissed_matches")
          .select("profile_2_id")
          .eq("dismissed_by_matchmaker_id", user.user.id)
          .eq("profile_1_id", profile.id);

        const dismissedSet = new Set<string>();
        dismissedData?.forEach((dismissed) => {
          dismissedSet.add(dismissed.profile_2_id);
        });
        setDismissedMatches(dismissedSet);
      }
    } catch (error) {
      console.error("smart_matcher.find_matches_failed");
    }
  };

  const handleViewDetails = (profile: any) => {
    setSelectedModalProfile(profile);
    setModalOpen(true);
  };

  const toggleCompatibilityDetails = (profileId: string) => {
    setExpandedCompatibility(prev => ({
      ...prev,
      [profileId]: !prev[profileId]
    }));
  };

  const handleCreateMatch = (profileId: string) => {
    if (!selectedProfile) return;

    // Find the profile to get its name
    const profile = matchingProfiles.find(p => p.id === profileId);
    if (!profile) return;

    // Open confirmation dialog
    setPendingMatch({
      profileId,
      profileName: profile.first_name || "Profile",
    });
    setConfirmDialogOpen(true);
  };

  const confirmCreateMatch = async () => {
    if (!selectedProfile || !pendingMatch) return;

    setCreatingMatchId(pendingMatch.profileId);
    setConfirmDialogOpen(false);

    try {
      const { data: user } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("matches")
        .insert({
          profile_1_id: selectedProfile.id,
          profile_2_id: pendingMatch.profileId,
          created_by_matchmaker_id: user.user?.id,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Match created successfully!", {
        description: `Created match between ${selectedProfile.first_name} and ${pendingMatch.profileName}`,
      });

      // Update existing matches
      setExistingMatches(prev => ({
        ...prev,
        [pendingMatch.profileId]: data
      }));

      setPendingMatch(null);
      setModalOpen(false);
    } catch (error: any) {
      console.error("smart_matcher.create_match_failed");
      toast.error("Failed to create match", {
        description: error.message || "Please try again",
      });
    } finally {
      setCreatingMatchId(null);
    }
  };

  const handleDismissMatch = async (profileId: string) => {
    if (!selectedProfile) return;

    try {
      const { data: user } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("dismissed_matches")
        .insert({
          dismissed_by_matchmaker_id: user.user?.id,
          profile_1_id: selectedProfile.id,
          profile_2_id: profileId,
        });

      if (error) throw error;

      // Add to dismissed set
      setDismissedMatches(prev => new Set(prev).add(profileId));

      toast.success("Match dismissed", {
        description: "This potential match has been dismissed",
      });
    } catch (error: any) {
      console.error("smart_matcher.dismiss_failed");
      toast.error("Failed to dismiss match", {
        description: error.message || "Please try again",
      });
    }
  };

  const handleUndismissMatch = async (profileId: string) => {
    if (!selectedProfile) return;

    try {
      const { data: user } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("dismissed_matches")
        .delete()
        .eq("dismissed_by_matchmaker_id", user.user?.id)
        .eq("profile_1_id", selectedProfile.id)
        .eq("profile_2_id", profileId);

      if (error) throw error;

      // Remove from dismissed set
      setDismissedMatches(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });

      toast.success("Match restored", {
        description: "This match has been restored",
      });
    } catch (error: any) {
      console.error("smart_matcher.restore_failed");
      toast.error("Failed to restore match", {
        description: error.message || "Please try again",
      });
    }
  };

  // Apply anonymization if admin
  const displayedSelectedProfile = isAdmin && selectedProfileDetails ? anonymizeProfile(selectedProfileDetails) : selectedProfileDetails;
  const signedSelectedPhoto = useSignedPhotoUrl(displayedSelectedProfile?.profile_picture_url ?? undefined);
  const displayedMatchingProfiles = isAdmin ? matchingProfiles.map(anonymizeProfile) : matchingProfiles;

  const perfectMatches = displayedMatchingProfiles.filter((p) => p.compatibility?.matchType === "perfect" && !dismissedMatches.has(p.id));
  const closeMatches = displayedMatchingProfiles.filter((p) => p.compatibility?.matchType === "close" && !dismissedMatches.has(p.id));
  const allMatches = displayedMatchingProfiles.filter((p) => !dismissedMatches.has(p.id)); // Show ALL non-dismissed matches
  const dismissed = displayedMatchingProfiles.filter((p) => dismissedMatches.has(p.id)); // Show dismissed matches

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const pageTitle = isAdmin ? "Smart Matcher (Anonymized)" : "Smart Matcher";
  const pageDescription = isAdmin
    ? "Select a profile to find their best matches (data anonymized for privacy)"
    : "Select a profile to find their best matches";

  return (
    <Layout title={pageTitle}>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">{pageTitle}</h1>
          <p className="text-muted-foreground">
            {pageDescription}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Panel - Selected Profile */}
          <div className="lg:col-span-4 space-y-4">
            <Card className="p-4">
              <h2 className="font-semibold mb-3">Select Profile</h2>
              <ProfileSelector
                selectedProfile={selectedProfile}
                onSelect={setSelectedProfile}
                anonymized={isAdmin}
              />
            </Card>

            {displayedSelectedProfile && (
              <Card className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-20 w-20">
                      <AvatarImage
                        src={signedSelectedPhoto ?? undefined}
                        alt={displayedSelectedProfile.first_name}
                        className={isAdmin ? "blur-md" : ""}
                      />
                      <AvatarFallback>
                        {displayedSelectedProfile.first_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {displayedSelectedProfile.first_name}, {displayedSelectedProfile.age}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {displayedSelectedProfile.location}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Gender:</span>{" "}
                      {displayedSelectedProfile.gender}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Religiosity:</span>{" "}
                      {displayedSelectedProfile.religiosity_level}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Education:</span>{" "}
                      {displayedSelectedProfile.education_level}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleViewDetails(displayedSelectedProfile)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Full Profile
                  </Button>

                  {selectedProfileDetails.seeking_preferences_weights && (
                    <div className="pt-3 border-t">
                      <PriorityBadges
                        weights={selectedProfileDetails.seeking_preferences_weights}
                        firstName={displayedSelectedProfile.first_name}
                      />
                    </div>
                  )}

                  <div className="pt-3 border-t space-y-2">
                    <p className="text-sm font-medium">Looking for:</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">
                        Age {selectedProfileDetails.seeking_age_min}-
                        {selectedProfileDetails.seeking_age_max}
                      </Badge>
                      {selectedProfileDetails.seeking_location_preference && (
                        <Badge variant="outline">
                          {selectedProfileDetails.seeking_location_preference}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Panel - Matching Profiles */}
          <div className="lg:col-span-8">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : !selectedProfile ? (
              <Alert>
                <AlertTitle>No profile selected</AlertTitle>
                <AlertDescription>
                  Select a profile from the left panel to see their matches
                </AlertDescription>
              </Alert>
            ) : (
              <Tabs defaultValue="perfect" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="perfect">
                    Perfect Matches ({perfectMatches.length})
                  </TabsTrigger>
                  <TabsTrigger value="close">
                    Close Matches ({closeMatches.length})
                  </TabsTrigger>
                  <TabsTrigger value="all">
                    All Potential ({allMatches.length})
                  </TabsTrigger>
                  <TabsTrigger value="dismissed">
                    Dismissed ({dismissed.length})
                  </TabsTrigger>
                </TabsList>

                <ScrollArea className="h-[calc(100vh-300px)] mt-4">
                  <TabsContent value="perfect" className="space-y-4">
                    {perfectMatches.length === 0 ? (
                      <Alert>
                        <AlertTitle>No perfect matches found</AlertTitle>
                        <AlertDescription>
                          Try viewing close matches or adjust filters
                        </AlertDescription>
                      </Alert>
                    ) : (
                      perfectMatches.map((profile) => (
                        <div key={profile.id} className="space-y-3">
                          <ProfileCard
                            profile={profile}
                            onViewDetails={handleViewDetails}
                            onCreateMatch={handleCreateMatch}
                            onDismiss={handleDismissMatch}
                            matchStatus={existingMatches[profile.id]?.status}
                            isLoading={creatingMatchId === profile.id}
                            isDismissed={dismissedMatches.has(profile.id)}
                            isAdmin={isAdmin}
                          />
                          <Collapsible
                            open={expandedCompatibility[profile.id]}
                            onOpenChange={() => toggleCompatibilityDetails(profile.id)}
                          >
                            <Card className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className={cn("text-2xl font-bold", getScoreColor(profile.compatibility.score))}>
                                    {profile.compatibility.score}%
                                  </span>
                                  <span className="text-sm text-muted-foreground ml-2">
                                    Compatibility Score
                                  </span>
                                </div>
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {expandedCompatibility[profile.id] ? "Hide Details" : "Show Details"}
                                  </Button>
                                </CollapsibleTrigger>
                              </div>
                              <CollapsibleContent className="mt-4">
                                <WeightedCompatibilityScore
                                  result={profile.compatibility}
                                  showDetails={true}
                                  profile1Name={displayedSelectedProfile?.first_name}
                                  profile2Name={profile.first_name}
                                />
                              </CollapsibleContent>
                            </Card>
                          </Collapsible>
                        </div>
                      ))
                    )}
                  </TabsContent>

                  <TabsContent value="close" className="space-y-4">
                    {closeMatches.length === 0 ? (
                      <Alert>
                        <AlertTitle>No close matches found</AlertTitle>
                        <AlertDescription>
                          Try viewing all potential matches
                        </AlertDescription>
                      </Alert>
                    ) : (
                      closeMatches.map((profile) => (
                        <div key={profile.id} className="space-y-3">
                          <ProfileCard
                            profile={profile}
                            onViewDetails={handleViewDetails}
                            onCreateMatch={handleCreateMatch}
                            onDismiss={handleDismissMatch}
                            matchStatus={existingMatches[profile.id]?.status}
                            isLoading={creatingMatchId === profile.id}
                            isDismissed={dismissedMatches.has(profile.id)}
                            isAdmin={isAdmin}
                          />
                          <Collapsible
                            open={expandedCompatibility[profile.id]}
                            onOpenChange={() => toggleCompatibilityDetails(profile.id)}
                          >
                            <Card className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className={cn("text-2xl font-bold", getScoreColor(profile.compatibility.score))}>
                                    {profile.compatibility.score}%
                                  </span>
                                  <span className="text-sm text-muted-foreground ml-2">
                                    Compatibility Score
                                  </span>
                                </div>
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {expandedCompatibility[profile.id] ? "Hide Details" : "Show Details"}
                                  </Button>
                                </CollapsibleTrigger>
                              </div>
                              <CollapsibleContent className="mt-4">
                                <WeightedCompatibilityScore
                                  result={profile.compatibility}
                                  showDetails={true}
                                  profile1Name={displayedSelectedProfile?.first_name}
                                  profile2Name={profile.first_name}
                                />
                              </CollapsibleContent>
                            </Card>
                          </Collapsible>
                        </div>
                      ))
                    )}
                  </TabsContent>

                  <TabsContent value="all" className="space-y-4">
                    {allMatches.length === 0 ? (
                      <Alert>
                        <AlertTitle>No matches found</AlertTitle>
                        <AlertDescription>
                          No profiles meet the minimum compatibility threshold
                        </AlertDescription>
                      </Alert>
                    ) : (
                      allMatches.map((profile) => (
                        <div key={profile.id} className="space-y-3">
                          <ProfileCard
                            profile={profile}
                            onViewDetails={handleViewDetails}
                            onCreateMatch={handleCreateMatch}
                            onDismiss={handleDismissMatch}
                            matchStatus={existingMatches[profile.id]?.status}
                            isLoading={creatingMatchId === profile.id}
                            isDismissed={dismissedMatches.has(profile.id)}
                            isAdmin={isAdmin}
                          />
                          <Collapsible
                            open={expandedCompatibility[profile.id]}
                            onOpenChange={() => toggleCompatibilityDetails(profile.id)}
                          >
                            <Card className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className={cn("text-2xl font-bold", getScoreColor(profile.compatibility.score))}>
                                    {profile.compatibility.score}%
                                  </span>
                                  <span className="text-sm text-muted-foreground ml-2">
                                    Compatibility Score
                                  </span>
                                </div>
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {expandedCompatibility[profile.id] ? "Hide Details" : "Show Details"}
                                  </Button>
                                </CollapsibleTrigger>
                              </div>
                              <CollapsibleContent className="mt-4">
                                <WeightedCompatibilityScore
                                  result={profile.compatibility}
                                  showDetails={true}
                                  profile1Name={displayedSelectedProfile?.first_name}
                                  profile2Name={profile.first_name}
                                />
                              </CollapsibleContent>
                            </Card>
                          </Collapsible>
                        </div>
                      ))
                    )}
                  </TabsContent>

                  <TabsContent value="dismissed" className="space-y-4">
                    {dismissed.length === 0 ? (
                      <Alert>
                        <AlertTitle>No dismissed matches</AlertTitle>
                        <AlertDescription>
                          You haven't dismissed any potential matches yet
                        </AlertDescription>
                      </Alert>
                    ) : (
                      dismissed.map((profile) => (
                        <div key={profile.id} className="space-y-3">
                          <ProfileCard
                            profile={profile}
                            onViewDetails={handleViewDetails}
                            onUndismiss={handleUndismissMatch}
                            isDismissed={true}
                            isAdmin={isAdmin}
                          />
                          <Collapsible
                            open={expandedCompatibility[profile.id]}
                            onOpenChange={() => toggleCompatibilityDetails(profile.id)}
                          >
                            <Card className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className={cn("text-2xl font-bold", getScoreColor(profile.compatibility.score))}>
                                    {profile.compatibility.score}%
                                  </span>
                                  <span className="text-sm text-muted-foreground ml-2">
                                    Compatibility Score
                                  </span>
                                </div>
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    {expandedCompatibility[profile.id] ? "Hide Details" : "Show Details"}
                                  </Button>
                                </CollapsibleTrigger>
                              </div>
                              <CollapsibleContent className="mt-4">
                                <WeightedCompatibilityScore
                                  result={profile.compatibility}
                                  showDetails={true}
                                  profile1Name={displayedSelectedProfile?.first_name}
                                  profile2Name={profile.first_name}
                                />
                              </CollapsibleContent>
                            </Card>
                          </Collapsible>
                        </div>
                      ))
                    )}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            )}
          </div>
        </div>
      </div>

      <ProfileDetailModal
        profileId={selectedModalProfile?.id || null}
        profile={selectedModalProfile}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreateMatch={handleCreateMatch}
        anonymized={isAdmin}
        isAdmin={isAdmin}
      />

      <MatchCreationConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={confirmCreateMatch}
        profile1Name={selectedProfile?.first_name || ""}
        profile2Name={pendingMatch?.profileName || ""}
        isAdmin={isAdmin}
      />
    </Layout>
  );
}
